import { type FormEvent, useRef, useState } from 'react';
import { ApiError, api } from '../api';
import { Turnstile, type TurnstileHandle } from '../components/Turnstile';
import { ErrorNote } from '../components/ui';

/**
 * Email-code login: enter your email, then the 6-digit code from your inbox, then (with 2FA on)
 * the code from an authenticator app or a recovery code.
 */
export function Login({ onLogin }: { onLogin: () => void }) {
  const [step, setStep] = useState<'email' | 'code' | 'two-factor' | 'waitlist'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Cloudflare Turnstile proves a person asked for the code. Each token works once.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstile = useRef<TurnstileHandle>(null);

  async function sendCode(e?: FormEvent) {
    e?.preventDefault();
    if (!turnstileToken) return;
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/auth/login', { email, turnstileToken });
      setStep('code');
      setCode('');
    } catch (err) {
      if (err instanceof ApiError && err.type === 'not_invited') setStep('waitlist');
      else setError(err);
    } finally {
      // The token is spent either way: get a fresh one so the user can try again.
      turnstile.current?.reset();
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ mfaRequired?: boolean }>('POST', '/auth/verify', { email, code });
      if (res.mfaRequired) {
        setStep('two-factor');
        setCode('');
        setBusy(false);
        return;
      }
      onLogin();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  async function verifySecondFactor(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/auth/2fa/verify', { code });
      onLogin();
    } catch (err) {
      // After 5 wrong codes (or 10 minutes) the login has to start again.
      if (err instanceof ApiError && err.status === 429) setStep('email');
      setError(err);
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth-card">
        <a className="auth-brand" href="https://vaartix.com">
          <img src="/vaartix-icon.svg" alt="" />
          vaartix
        </a>
        <div className="auth-box">
          {step === 'waitlist' ? (
            <>
              <h1>You're not on the list yet</h1>
              <p>
                Vaartix is in private beta and <strong>{email}</strong> hasn't been invited yet.
                Request early access and we'll email you as soon as there's a place for you.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <a className="btn btn-primary" href="https://vaartix.com/#early-access">
                  Request early access
                </a>
                <button type="button" className="link-btn" onClick={() => setStep('email')}>
                  Use a different email
                </button>
              </div>
            </>
          ) : step === 'two-factor' ? (
            <>
              <h1>Two-factor authentication</h1>
              <p>
                Enter the 6-digit code from your authenticator app, or one of your recovery codes.
              </p>
              <form onSubmit={verifySecondFactor}>
                <div className="field">
                  <label htmlFor="totp">Authentication code</label>
                  <input
                    id="totp"
                    className="input code-input"
                    autoComplete="one-time-code"
                    autoCapitalize="none"
                    spellCheck={false}
                    maxLength={11}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.trim())}
                  />
                </div>
                <ErrorNote error={error} />
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={busy || code.length < 6}
                >
                  {busy ? 'Checking…' : 'Log in'}
                </button>
                <button type="button" className="link-btn" onClick={() => setStep('email')}>
                  Start again
                </button>
              </form>
            </>
          ) : step === 'email' ? (
            <>
              <h1>Log in to Vaartix</h1>
              <p>We'll email you a 6-digit code. No password needed.</p>
              <form onSubmit={sendCode}>
                <div className="field">
                  <label htmlFor="email">Work email</label>
                  <input
                    id="email"
                    className="input"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="you@company.in"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Turnstile ref={turnstile} action="login" onToken={setTurnstileToken} />
                <ErrorNote error={error} />
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={busy || !turnstileToken}
                >
                  {busy
                    ? 'Sending…'
                    : turnstileToken
                      ? 'Email me a code'
                      : 'Checking your browser…'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1>Check your email</h1>
              <p>
                We sent a code to <strong>{email}</strong>. It expires in 10 minutes.
              </p>
              <form onSubmit={verify}>
                <div className="field">
                  <label htmlFor="code">6-digit code</label>
                  <input
                    id="code"
                    className="input code-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <ErrorNote error={error} />
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={busy || code.length !== 6}
                >
                  {busy ? 'Checking…' : 'Log in'}
                </button>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button type="button" className="link-btn" onClick={() => setStep('email')}>
                    Use a different email
                  </button>
                  <button
                    type="button"
                    className="link-btn"
                    disabled={busy || !turnstileToken}
                    onClick={() => void sendCode()}
                  >
                    Send a new code
                  </button>
                </div>
                {/* A new code needs a new check too. */}
                <Turnstile ref={turnstile} action="login" onToken={setTurnstileToken} />
              </form>
            </>
          )}
        </div>
        <p className="auth-foot">
          Invited to the beta? Logging in creates your account. By continuing you agree to the{' '}
          <a href="https://vaartix.com/terms">Terms</a> and{' '}
          <a href="https://vaartix.com/acceptable-use">Acceptable Use Policy</a>.
        </p>
        <p className="auth-foot">
          Vaartix by{' '}
          <a href="https://napdesigns.com" target="_blank" rel="noopener">
            NAP Designs
          </a>
          , Bangalore, India
        </p>
      </div>
    </div>
  );
}
